import React, { CSSProperties, FC, ReactElement, useEffect, useRef, useState } from 'react';

import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { airplaneStates } from '../../../store/slices/highflyerAirPlane/types';

import styles from './Airplane.module.css';

export interface AirplaneInterface {
  airplaneCurrentState: airplaneStates;
  isAnimating: boolean;
  motionID: number;
  airplaneWH: {
    airplaneBoxWidth: number;
    airplaneBoxHeight: number;
    planeWidth: number;
    PlaneHeight: number;
    flagWidth: number;
    flagHeight: number;
    scaleValue: number;
    propellerWidth: number;
    propellerHeight: number;
  };
  theme: string;
  inDesktopMode: boolean;
  isFlagAvailable: boolean;
  flagURL:string;
  planeURL:string;
}

export const Airplane: FC<AirplaneInterface> = ({
  airplaneCurrentState,
  motionID,
  isAnimating,
  airplaneWH,
  theme,
  inDesktopMode,
  isFlagAvailable,
  flagURL,
  planeURL
}): ReactElement | null => {
  const airplane = useRef<SVGAnimateElement | null>(null);
  const flag = useRef<SVGAnimateElement | null>(null);
  const shadowX = useRef<SVGAnimateElement | null>(null);
  const shadowY = useRef<SVGAnimateElement | null>(null);
  const shadowXY = useRef<SVGAnimateElement | null>(null);
  const [visibleGroup, setVisibleGroup] = useState('group1');

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleGroup((prev) => (prev === 'group1' ? 'group2' : 'group1'));
    }, 50);
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  useEffect(() => {
    if (!inDesktopMode && airplaneCurrentState === airplaneStates.FLYING) {
      airplane.current?.beginElement();
      flag.current?.beginElement();
      shadowX.current?.beginElement();
      shadowY.current?.beginElement();
      shadowXY.current?.beginElement();
    }
  }, [airplaneCurrentState, airplaneWH]);

  useEffect(() => {
    if (inDesktopMode && airplaneCurrentState === airplaneStates.FLYING) {
      airplane.current?.beginElement();
      flag.current?.beginElement();
      shadowX.current?.beginElement();
      shadowY.current?.beginElement();
      shadowXY.current?.beginElement();
    }
  }, [airplaneCurrentState, inDesktopMode]);

  const flyingPlaneMods = useModifiers(styles, 'flying_plane', {
    [airplaneCurrentState]: true,
  });

  const planeShadowMods = useModifiers(styles, 'planeShadow', {
    [airplaneCurrentState]: true,
  });

  const flagMods = useModifiers(styles, 'flag', {
    [airplaneCurrentState]: true,
  });

  if (!isAnimating) return null;

  const {
    planeWidth,
    PlaneHeight,
    flagWidth,
    flagHeight,
    airplaneBoxWidth,
    airplaneBoxHeight,
    propellerWidth,
    propellerHeight,
    scaleValue
  } = airplaneWH;
  const flagX = -flagWidth * 0.1;
  const flagY = -flagHeight * 0.5;
  const planeX = -planeWidth * 0.12;
  const planeY = -PlaneHeight * 0.85;
  const propellerX = propellerWidth * 2.13;
  const propellerY = -propellerHeight * 0.72;
  const shadowCX = planeWidth * 0.5;
  const shadowCY = airplaneBoxHeight - 5 * scaleValue;
  const shadowRX = planeWidth * 0.5;
  const shadowRy = PlaneHeight * 0.05;
  

  return (
    <g>
      <g id="shadowContainer">
        <ellipse
          id="planeShadow"
          className={`${styles.planeShadow} ${planeShadowMods}`}
          cx={shadowCX}
          cy={shadowCY}
          rx={shadowRX}
          ry={shadowRy}
          fill="black"
          fillOpacity="0.4"
        >
          <animate ref={shadowX} attributeName="rx" values={`${shadowRX};0;0`} dur="3.5s" fill="freeze" />
          <animate ref={shadowY} attributeName="ry" values={`${shadowRy};0;0`} dur="3.5s" fill="freeze" />
          <animate
            ref={shadowXY}
            attributeName="cx"
            values={`${shadowCX};${airplaneBoxWidth}`}
            dur="3.5s"
            fill="freeze"
          />
        </ellipse>
      </g>

      {theme !== 'default' && isFlagAvailable && (
        <g id="flags">
          <g className={`${styles.flying_plane} ${flyingPlaneMods}`}>
            <g clipPath="url(#flagMask)">
              <image
                className={`${styles.flagSprite} ${flagMods}`}
                style={{ '--flagPX': -flagWidth + 'px' } as CSSProperties}
                xlinkHref={flagURL}
                width={flagWidth}
                height={flagHeight}
                x={flagX}
                y={flagY}
              />
            </g>
          </g>
        </g>
      )}

      <g id="airplanes">
        <g className={`${styles.flying_plane} ${flyingPlaneMods}`}>
          <image
            className={styles.airplaneSprite}
            xlinkHref={planeURL}
            width={planeWidth}
            height={PlaneHeight}
            x={planeX}
            y={planeY}
          />
          <svg
            x={propellerX}
            y={propellerY}
            width={propellerWidth * 0.25}
            height={propellerHeight}
            viewBox="0 0 71 135"
            shapeRendering="geometricPrecision"
            textRendering="geometricPrecision"
          >
            <path
              id="propeller21-s-path1"
              d="M0.361512,43.3275C-0.263834,9.05338,14.8402,-7.87348,34.0973,5.52026s35.3751,52.03624,36.0005,86.31034c.6254,34.2744-14.4787,51.2014-33.7358,37.8074s-35.375088-52.0364-36.000488-86.3105ZM35.849,101.525c10.5336,7.326,18.7953-1.9327,18.4533-20.6805-.3421-18.7477-9.1585-39.885-19.6921-47.2113s-18.7953,1.9327-18.4533,20.6804c.3422,18.7477,9.1586,39.8849,19.6921,47.2114Z"
              fill="#fff"
              fillOpacity="0.2"
            />
            <g opacity={visibleGroup === 'group1' ? 1 : 0}>
              <path
                id="propeller21-s-path2"
                d="M114.5,15.5c-3.292-4.0619-6.911-7.49916-10.5-10.00012C84.5004,-8.08693,69.5859,9.50019,70.1979,43.0953c.0651,3.5783.298,7.2039.6848,10.8436l15.351,4.1133c-.1018-1.313-.1656-2.6199-.1892-3.9155-.3432-18.8421,7.9455-28.1478,18.5135-20.7845c1.366.952,2.704,2.1361,3.998,3.5171L114.5,15.5Z"
                transform="translate(-70 0)"
                fill="#fff"
                fillOpacity="0.5"
              />
              <path
                id="propeller21-s-path3"
                d="M100.518,96.6176c1.687,2.0136,3.459,3.6974,5.282,4.9684c10.568,7.363,18.857-1.9425,18.514-20.7848-.028-1.5336-.113-3.0832-.25-4.6399l15.332,4.108c.434,3.8847.695,7.756.764,11.5734.628,34.4463-14.525,51.4593-33.845,37.9973-4.046-2.819-7.9545-6.752-11.6107-11.524l5.8137-21.6984Z"
                transform="translate(-70 0)"
                fill="#fff"
                fillOpacity="0.5"
              />
            </g>
            <g opacity={visibleGroup === 'group2' ? 1 : 0}>
              <path
                id="propeller21-s-path4"
                d="M69,78C66,55,57.5,33,46.3684,18l-5.743,21.5651C47.5,48,52.2086,61.3688,53.8532,73.9165L69,78Z"
                fill="#fff"
                fillOpacity="0.5"
              />
              <path
                id="propeller21-s-path5"
                d="M27.4658,92.4587c-5.2667-8.0241-9.3193-19.2408-10.8198-30.4168L1.5,57.9586C4.32261,78.6347,11.9105,99.3568,21.7291,114l5.7367-21.5413Z"
                fill="#fff"
                fillOpacity="0.5"
              />
            </g>
          </svg>
        </g>
      </g>

      <defs>
        <clipPath id="propellerMask">
          <rect x={propellerX} y={propellerY} width={propellerWidth / 2} height={propellerHeight} />
        </clipPath>

        <clipPath id="flagMask">
          <rect x={flagX} y={flagY} width={Math.floor(flagWidth / 10) - 1} height={flagHeight} />
        </clipPath>
      </defs>

      <animateMotion
        ref={airplane}
        attributeType="XML"
        xlinkHref="#airplanes"
        attributeName="motion"
        dur="3s"
        begin="0"
        fill="freeze"
        keyPoints="0;1;1"
        keyTimes="0;1;1"
        calcMode="linear"
      >
        <mpath xlinkHref={`#${motionID.toString()}`}></mpath>
      </animateMotion>

      <animateMotion
        ref={flag}
        attributeType="XML"
        xlinkHref="#flags"
        attributeName="motion"
        dur="3s"
        begin="0"
        fill="freeze"
        keyPoints="0;1;1"
        keyTimes="0;1;1"
        calcMode="linear"
      >
        <mpath xlinkHref={`#${motionID.toString()}`}></mpath>
      </animateMotion>
    </g>
  );
};
